export interface Address {
  name?: string | null
  name2?: string | null
  name3?: string | null
  street?: string | null
  houseNumber?: string | null
  street4?: string | null
  zip?: string | null
  region?: string | null
  city?: string | null
  partnerNumber?: string | null
  country?: string | null
  [key: string]: string | null | undefined
}

export interface Region {
  id?: string
  label: string
}

export type RegionMap = Record<string, Region>

export type Regions = RegionMap | Region[] | null

export interface AddressResult {
  name: string
  address: string
  street4: string
  location: string
  displayText: string
}

interface MergeDataParams {
  address: Address
  keys: string[]
  regions: Regions
  ignores: string[]
  separator: string
}

interface GenerateAddressObjectParams {
  address: Address
  regions: Regions
  ignores: string[]
  separator: string
  scheme: string
}

const mergeData = ({
  address,
  keys,
  regions,
  ignores,
  separator,
}: MergeDataParams): string =>
  keys
    .reduce((result: string[], key: string) => {
      if (key === 'region') {
        const regionKey = address[key]
        const region = Array.isArray(regions)
          ? regions.find((r) => r && r.id === regionKey)
          : regions?.[regionKey] // fallback for object format
        if (region) {
          return [...result, region.label]
        }
        if (address[key] && !ignores.includes(key)) {
          // Fallback to raw region value if lookup fails
          return [...result, address[key]]
        }
      } else if (address[key] && !ignores.includes(key)) {
        return [...result, address[key]]
      }
      return result
    }, [])
    .join(separator)

const generateAddressObject = ({
  address,
  regions,
  ignores,
  separator,
  scheme,
}: GenerateAddressObjectParams): AddressResult => {
  const locationKeys: string[] = scheme === 'USA'
    ? ['city', 'region', 'zip']
    : ['zip', 'region', 'city']
  const displayTextKeys: string[] = [
    'partnerNumber',
    'name',
    'name2',
    'name3',
    'street',
    'houseNumber',
    'street4',
    ...locationKeys,
    'country',
  ]

  return {
    name: mergeData({
      address,
      keys: ['name', 'name2', 'name3'],
      regions,
      ignores,
      separator,
    }),
    address: mergeData({
      address,
      keys: ['street', 'houseNumber'],
      regions,
      ignores,
      separator,
    }),
    street4: mergeData({
      address,
      keys: ['street4'],
      regions,
      ignores,
      separator,
    }),
    location: mergeData({
      address,
      keys: locationKeys,
      regions,
      ignores,
      separator,
    }),
    displayText: mergeData({
      address,
      keys: displayTextKeys,
      regions,
      ignores,
      separator,
    }),
  }
}

export const getAddress = (
  address: Address,
  regions: Regions = {},
  ignores: string[] = [],
  separator = ' ',
  isUSAAddressScheme = false,
): AddressResult => {
  const scheme = isUSAAddressScheme ? 'USA' : 'default'
  return generateAddressObject({
    address,
    regions,
    ignores,
    separator,
    scheme,
  })
}
